/* eslint-disable @atlaskit/ui-styling-standard/no-dynamic-styles, @atlaskit/ui-styling-standard/no-imported-style-values */
/** @jsx jsx */
/** @jsxFrag */
import type { FC } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import type { ReactComponentLike } from 'prop-types';
import { styled, css, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { VIEW_PAGE_TITLE_EXPERIENCE, ExperienceSuccess } from '@confluence/experience-tracker';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { LazyEmojiComponentLoader } from '@confluence/emoji-title';
import {
	EMOJI_SIZE_LARGE,
	EMOJI_SIZE_MEDIUM,
} from '@confluence/emoji-title/entry-points/constants';
import { ContentClassificationTag } from '@confluence/data-classification';
import {
	TitleAlignmentType,
	useTitleContentPropertiesForPublishedPage,
	useIsFixedWidthImageOption,
	type TitleContentPropertiesType,
} from '@confluence/custom-sites-extensions';
import { CopyLinkButtonLoader } from '@confluence/share';
import { useContentClassification } from '@confluence/data-classification/entry-points/queries';

import { CONTENT_TITLE_METRIC } from '../perf.config';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const HeadingWrapper = styled.div<{
	isCustomSitesPageTitleFFOn?: boolean;
	hasBanner?: boolean;
}>({
	position: ({ isCustomSitesPageTitleFFOn }) => (isCustomSitesPageTitleFFOn ? 'relative' : 'unset'),
	marginBottom: ({ isCustomSitesPageTitleFFOn, hasBanner }) =>
		isCustomSitesPageTitleFFOn && hasBanner ? token('space.300', '24px') : 'initial',
});

// TODO: Refactor to a common location
// See https://product-fabric.atlassian.net/browse/CFE-2847

/* please update next/packages/content-title/src/ContentTitlePlaceholder.js if you change anything here */
const styledContentTitle = css({
	display: 'inline-block',
	boxSizing: 'border-box',
	minHeight: '120px',
	width: '100%',
	maxWidth: '100%',
});

const centerTitleStyles = css({
	display: 'block',
	margin: '0 auto',
	wordBreak: 'break-word',
	wordWrap: 'break-word',
	maxWidth: '760px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Ignored via go/DSP-18766
const StyledH1 = styled.h1<{
	onClick: React.ComponentProps<'h1'>['onClick'];
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
	isPageContentFullWidth?: boolean;
	isCustomSitesPageTitleFFOn?: boolean;
	isEmbeddedPage?: boolean;
	hasEmoji?: boolean;
	isFixedWidthImageOption?: boolean;
}>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&#title-text': {
			alignItems: ({ isCustomSitesPageTitleFFOn }) =>
				isCustomSitesPageTitleFFOn ? 'center' : 'initial',
			font: token('font.heading.xlarge'),
			fontWeight: 'normal',
			flexDirection: ({
				isFixedWidthImageOption,
				isCustomSitesPageTitleFFOn,
				hasEmoji,
				isPageContentFullWidth,
				hasCoverPicture,
				isTitleCenterAligned,
			}) =>
				(isTitleCenterAligned && isCustomSitesPageTitleFFOn) ||
				(isFixedWidthImageOption &&
					hasEmoji &&
					!isTitleCenterAligned &&
					isCustomSitesPageTitleFFOn &&
					isPageContentFullWidth &&
					hasCoverPicture)
					? 'column'
					: 'unset',
			marginTop: ({ isCustomSitesPageTitleFFOn, isEmbeddedPage, hasCoverPicture }) =>
				isCustomSitesPageTitleFFOn && isEmbeddedPage && hasCoverPicture
					? token('space.negative.300', '-24px')
					: '0',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '28px',
			lineHeight: '1.25',
			gap: ({ isTitleCenterAligned, hasCoverPicture }) =>
				isTitleCenterAligned && !hasCoverPicture ? token('space.200', '16px') : 'initial',
		},
		margin: '0',
		color: token('color.text', '#333'),
		wordBreak: 'break-word',
		wordWrap: 'break-word',
		whiteSpace: 'preserve',
		/* update next/packages/content-title/src/ContentTitlePlaceholder.js when this changes */
		minHeight: '35px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingTop: '5px',
		cursor: 'pointer',
		display: 'flex',
		pointerEvents: ({ onClick }) => (onClick ? 'default' : 'none'),
	},
	({ isTitleCenterAligned }) =>
		isTitleCenterAligned
			? 'justify-content: center; text-align: center; flex-direction: column; align-items: center'
			: '',
);

const emojiFullWidthLeftAlignWrapper = css({
	display: 'flex',
	justifyContent: 'initial',
	width: '800px',
	paddingLeft: token('space.500', '40px'),
});

const titleTextFullWidthLeftAlignWrapper = css({
	display: 'flex',
	justifyContent: 'flex-start',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const EmojiWrapper = styled.div<{
	isCustomSitesPageTitleFFOn: boolean;
	isTitleCenterAligned?: boolean;
	hasCoverPicture?: boolean;
}>({
	paddingRight: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? '0px' : '10px'),
	display: 'flex',
	justifyContent: 'center',
	maxWidth: ({ isCustomSitesPageTitleFFOn, hasCoverPicture }) =>
		isCustomSitesPageTitleFFOn
			? hasCoverPicture
				? `${EMOJI_SIZE_LARGE}px`
				: `${EMOJI_SIZE_MEDIUM}px`
			: '26px',
	alignItems: ({ isTitleCenterAligned }) => (isTitleCenterAligned ? 'initial' : 'center'),
	position: ({ isCustomSitesPageTitleFFOn, hasCoverPicture }) =>
		isCustomSitesPageTitleFFOn && hasCoverPicture ? 'absolute' : 'unset',
	top: ({ isCustomSitesPageTitleFFOn, hasCoverPicture }) =>
		isCustomSitesPageTitleFFOn && hasCoverPicture ? '-75px' : 'unset',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> * span': {
		display: ({ isCustomSitesPageTitleFFOn }) => (isCustomSitesPageTitleFFOn ? 'flex' : undefined),
		justifyContent: ({ isCustomSitesPageTitleFFOn }) =>
			isCustomSitesPageTitleFFOn ? 'center' : 'initial',
		width: ({ isCustomSitesPageTitleFFOn, hasCoverPicture }) =>
			isCustomSitesPageTitleFFOn
				? hasCoverPicture
					? `${EMOJI_SIZE_LARGE}px`
					: `${EMOJI_SIZE_MEDIUM}px`
				: '26px',
		height: ({ isCustomSitesPageTitleFFOn, hasCoverPicture }) =>
			isCustomSitesPageTitleFFOn
				? hasCoverPicture
					? `${EMOJI_SIZE_LARGE}px`
					: `${EMOJI_SIZE_MEDIUM}px`
				: '26px',
		overflow: ({ isCustomSitesPageTitleFFOn }) =>
			isCustomSitesPageTitleFFOn ? 'hidden' : 'initial',
		alignItems: ({ isCustomSitesPageTitleFFOn }) =>
			isCustomSitesPageTitleFFOn ? 'center' : 'initial',
	},
});

const titleCenteredMargin = (isTitleCenterAligned) => {
	return isTitleCenterAligned ? '-5px' : 'unset';
};
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const TitleTextWrapper = styled.span<{
	isTitleCenterAligned?: boolean;
	hasEmojiAndCoverImage?: boolean;
}>({
	marginTop: ({ isTitleCenterAligned, hasEmojiAndCoverImage }) =>
		hasEmojiAndCoverImage ? '26px' : titleCenteredMargin(isTitleCenterAligned),
	/* Slide out and show "copy link" button next to title on hover */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'&:hover > span, &:focus-within > span': {
		opacity: '1',
		transform: 'none',
		transformDuration: '0.1s',
	},
});

const copyLinkWrapper = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '15px',
	// The following styles hide Copy Link button initially
	marginInlineStart: token('space.100', '0.5rem'),
	opacity: '0',
	position: 'absolute',
	transform: 'translateX(-4px)',
	transition: 'opacity 0.2s ease-out 0s, transform 0.2s ease-out 0s',
});

type ContentTitleProps = {
	banner?: React.ReactNode | null;
	centerTitle: boolean;
	lookAndFeel?: {
		content: {
			header: any;
		};
		headings: any;
	};
	prefix?: string | '';
	title?: string | '';
	children?: React.ReactNode | null;
	hasEmoji: boolean;
	emojiId: string | '';
	contentId: string;
	contentType: string;
	spaceKey: string;
	loading: boolean;
	headingContainer?:
		| React.ComponentType<{
				children: React.ReactNode;
				showInlineCommentButton?: boolean;
		  }>
		| ReactComponentLike;
	hasCoverPicture?: boolean;
	isPageContentFullWidth?: boolean;
	isEmbeddedPage?: boolean;
	titleContentProperties: TitleContentPropertiesType;
	isCustomSitesPageTitleFFOn: boolean;
};

type NullableQueryParams = {
	param: string | string[] | null;
};

const isURLVisible = () => window.top === window.self;

export const ContentTitleComponent: FC<ContentTitleProps> = ({
	lookAndFeel,
	banner,
	title,
	children,
	centerTitle,
	prefix,
	hasEmoji,
	emojiId,
	contentId,
	spaceKey,
	contentType,
	loading,
	headingContainer: HeadingContainer = React.Fragment,
	hasCoverPicture,
	isPageContentFullWidth,
	isEmbeddedPage,
	titleContentProperties,
	isCustomSitesPageTitleFFOn,
}) => {
	const [emojiTitle, setEmojiTitle] = useState<string | null>(emojiId);
	useEffect(() => {
		// for page that does not have emoji, we want to set emoji to null
		// so the emoji doesn't appear twice when user navigate between page via page tree
		if (!hasEmoji) {
			setEmojiTitle(null);
		} else {
			setEmojiTitle(emojiId);
		}
	}, [hasEmoji, emojiId]);

	const { getQueryParams, setQueryParams } = useContext(RoutesContext);

	const { coverPictureWidth } = useTitleContentPropertiesForPublishedPage({
		contentId,
		isCustomSitesPageTitleFFOn,
	});
	const isFixedWidthImageOption = useIsFixedWidthImageOption({ coverPictureWidth });

	const isTitleCenterAligned =
		isCustomSitesPageTitleFFOn &&
		titleContentProperties?.titleLayoutAlignment == TitleAlignmentType.CENTER;

	/**
	 * The feature of clearing query params by clicking title is only useful when it's used in standalone Confluence with the URL visible.
	 * When a page gets embedded e.g. rendered within iframe macro, user won't be able to see the effect from clearing query params and the mouse pointer and clicking lead to a confusing user experience.
	 */
	const clearQueryParams = isURLVisible()
		? () => {
				const queryParams = getQueryParams() as NullableQueryParams;
				if (!queryParams) return;
				Object.keys(queryParams).forEach((k) => (queryParams[k] = null));
				setQueryParams(queryParams);
			}
		: undefined;

	const contentClassificationMetadata = useContentClassification({
		contentId,
		spaceKeyOverride: spaceKey,
		contentStatusContext: 'current-only',
	});

	const isLeftAlignedTitleWithFixedWidthImageAndEmoji =
		isFixedWidthImageOption &&
		hasEmoji &&
		!isTitleCenterAligned &&
		hasCoverPicture &&
		isPageContentFullWidth &&
		isCustomSitesPageTitleFFOn;

	const heading = (
		<StyledH1
			id="title-text"
			data-test-id="title-text"
			data-testid="title-text"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={lookAndFeel?.headings}
			onClick={clearQueryParams}
			isTitleCenterAligned={isTitleCenterAligned}
			hasCoverPicture={hasCoverPicture}
			isPageContentFullWidth={isPageContentFullWidth}
			isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
			isFixedWidthImageOption={isFixedWidthImageOption}
			isEmbeddedPage={isEmbeddedPage}
			hasEmoji={hasEmoji}
		>
			{emojiTitle && (
				<>
					{isLeftAlignedTitleWithFixedWidthImageAndEmoji ? (
						<div css={emojiFullWidthLeftAlignWrapper}>
							<EmojiWrapper
								isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
								isTitleCenterAligned={isTitleCenterAligned}
								hasCoverPicture={hasCoverPicture}
							>
								<span data-testid="emoji-title">
									<LazyEmojiComponentLoader
										emoji={emojiTitle}
										height={
											isCustomSitesPageTitleFFOn
												? hasCoverPicture
													? EMOJI_SIZE_LARGE
													: EMOJI_SIZE_MEDIUM
												: 26
										}
										hasCoverPicture={hasCoverPicture}
										context="viewPage"
										showTooltip
										renderResourcedEmoji={isCustomSitesPageTitleFFOn}
										isPageTitleComponent
									/>
								</span>
							</EmojiWrapper>
						</div>
					) : (
						<EmojiWrapper
							isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
							isTitleCenterAligned={isTitleCenterAligned}
							hasCoverPicture={hasCoverPicture}
						>
							<span data-testid="emoji-title">
								<LazyEmojiComponentLoader
									emoji={emojiTitle}
									height={
										isCustomSitesPageTitleFFOn
											? hasCoverPicture
												? EMOJI_SIZE_LARGE
												: EMOJI_SIZE_MEDIUM
											: 26
									}
									hasCoverPicture={hasCoverPicture}
									context="viewPage"
									showTooltip
									renderResourcedEmoji={isCustomSitesPageTitleFFOn}
									isPageTitleComponent
								/>
							</span>
						</EmojiWrapper>
					)}
				</>
			)}
			{isLeftAlignedTitleWithFixedWidthImageAndEmoji ? (
				<div css={titleTextFullWidthLeftAlignWrapper} data-testid="fullwidthleftalign">
					<TitleTextWrapper
						isTitleCenterAligned={isTitleCenterAligned}
						hasEmojiAndCoverImage={hasCoverPicture && isCustomSitesPageTitleFFOn}
					>
						{prefix ? `${prefix} - ${title}` : title}
						<span css={copyLinkWrapper} data-testId="copylink-wrapper">
							<CopyLinkButtonLoader
								shouldResetOnHover
								contentId={contentId}
								spaceKey={spaceKey}
								contentType={contentType}
								pageMode="view"
								source="pageTitle"
								buttonAppearance="subtle-link"
								tooltipPosition="top"
							/>
						</span>
					</TitleTextWrapper>
				</div>
			) : (
				<TitleTextWrapper
					isTitleCenterAligned={isTitleCenterAligned}
					hasEmojiAndCoverImage={hasCoverPicture && isCustomSitesPageTitleFFOn}
				>
					{prefix ? `${prefix} - ${title}` : title}
					<span css={copyLinkWrapper} data-testId="copylink-wrapper">
						<CopyLinkButtonLoader
							shouldResetOnHover
							contentId={contentId}
							spaceKey={spaceKey}
							contentType={contentType}
							pageMode="view"
							source="pageTitle"
							buttonAppearance="subtle-link"
							tooltipPosition="top"
						/>
					</span>
				</TitleTextWrapper>
			)}
		</StyledH1>
	);

	return (
		<div data-testid="title-wrapper">
			<div
				css={[styledContentTitle, centerTitle && centerTitleStyles]}
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
					...lookAndFeel?.content?.header,
				}}
				data-test-id="page-content-header"
				data-testid="page-content-header"
			>
				{(!isCustomSitesPageTitleFFOn && banner) || null}
				{!isCustomSitesPageTitleFFOn && (
					<ContentClassificationTag
						contentId={contentId}
						contentType={contentType}
						contentClassificationMetadata={contentClassificationMetadata}
						isEditMode={false}
					/>
				)}
				<HeadingWrapper
					isCustomSitesPageTitleFFOn={isCustomSitesPageTitleFFOn}
					hasBanner={!!banner}
				>
					<HeadingContainer showInlineCommentButton>{heading}</HeadingContainer>
				</HeadingWrapper>
				{(isCustomSitesPageTitleFFOn && banner) || null}
				{!loading && <PageSegmentLoadEnd key={contentId} metric={CONTENT_TITLE_METRIC} />}
				{children}
				<ExperienceSuccess name={VIEW_PAGE_TITLE_EXPERIENCE} />
			</div>
		</div>
	);
};
